// 4-pillar Content Hubs: each connects programs, articles and calculators for SEO + navigation.
// Consumed by pages/ContentHubPage.jsx via the :hubSlug route param.

export const CONTENT_HUBS = {
  'diabetes-metabolic-health': {
    slug: 'diabetes-metabolic-health',
    icon: 'Activity',
    eyebrow: 'Health Hub',
    title: 'Diabetes & Metabolic Health',
    gradient: 'from-blue-500 to-cyan-500',
    accent: 'text-blue-400',
    badgeBg: 'bg-blue-500/10',
    badgeBorder: 'border-blue-500/30',
    metaTitle: 'Diabetes & Metabolic Health Hub | Doctor-Led Diabetes Nutrition',
    metaDescription: 'Everything on doctor-led diabetes and metabolic health in one place: the diabetes nutrition program, a diabetes diet guide for parents, evidence-based articles and free calculators from TCUBE FITNESS.',
    intro: 'Doctor-led, evidence-based support for diabetes, prediabetes and metabolic health, bringing together our program, practical guides and free tools. Nutrition supports blood sugar management and complements, never replaces, your medical care.',
    programs: [
      { label: 'Doctor-Led Diabetes & Clinical Nutrition', to: '/medical-nutrition-therapy', desc: 'Personalized, doctor-supervised nutrition for diabetes, prediabetes, insulin resistance and metabolic health.' },
      { label: 'Doctor-Led Prediabetes Program', to: '/prediabetes-program', desc: 'Act early with a personalized diet and lifestyle plan to support healthier blood sugar.' },
      { label: 'Diabetes Diet for Parents', to: '/diabetes-diet-for-parents', desc: 'Practical, doctor-informed diabetes nutrition help for your mother or father.' },
      { label: 'Doctor-Led Weight Management', to: '/doctor-led-weight-management', desc: 'Medically informed, sustainable weight management for metabolic health.' },
    ],
    articles: [
      { label: 'Diabetes Diet Plan: Indian Foods, Meals & What to Eat', to: '/blog/diabetes-diet-plan-indian-foods' },
      { label: 'Rice, Roti and Fruit in Diabetes', to: '/blog/rice-roti-fruit-and-diabetes' },
      { label: 'Prediabetes Diet and Lifestyle', to: '/blog/prediabetes-diet-and-lifestyle' },
      { label: 'Gestational Diabetes Diet in Pregnancy', to: '/blog/gestational-diabetes-diet-pregnancy' },
      { label: 'Insulin Resistance: Symptoms, Diet & Exercise', to: '/blog/insulin-resistance-symptoms-diet-exercise' },
      { label: 'Visceral Fat & Waist Size: Why They Matter', to: '/blog/visceral-fat-waist-size-health' },
    ],
    calculators: [
      { label: 'BMI Calculator', to: '/calculators/bmi' },
      { label: 'TDEE Calculator', to: '/calculators/tdee' },
      { label: 'Waist-to-Hip Ratio', to: '/calculators/waist-to-hip' },
      { label: 'Calorie Deficit', to: '/calculators/calorie-deficit' },
    ],
    tool: { label: 'Full Body Health Analysis', to: '/health-analysis' },
    faqs: [
      { question: 'Can nutrition help manage diabetes?', answer: 'Evidence-based nutrition and lifestyle change, guided alongside your treating doctor, can support blood sugar management. It complements, and never replaces, prescribed medication and monitoring.' },
      { question: 'Where should I start?', answer: 'Begin with the Full Body Health Analysis or a free consultation. We then recommend the most suitable doctor-led starting point for your health.' },
    ],
  },

  'metabolic-weight-health': {
    slug: 'metabolic-weight-health',
    icon: 'Activity',
    eyebrow: 'Health Hub',
    title: 'Metabolic & Weight Health',
    gradient: 'from-teal-500 to-emerald-500',
    accent: 'text-emerald-400',
    badgeBg: 'bg-emerald-500/10',
    badgeBorder: 'border-emerald-500/30',
    metaTitle: 'Metabolic & Weight Health Hub | Diabetes, Weight & Metabolism',
    metaDescription: 'Doctor-led guidance for diabetes, prediabetes, thyroid, weight and metabolic health. Explore programs, evidence-based guides and free calculators from TCUBE FITNESS.',
    intro: 'Sustainable, doctor-guided support for blood sugar, weight and long-term metabolic health. Everything here connects clinical guidance with practical tools you can act on today.',
    programs: [
      { label: 'Diabetes Management & Clinical Nutrition', to: '/medical-nutrition-therapy', desc: 'Doctor-supervised nutrition for diabetes, prediabetes, thyroid and metabolic conditions.' },
      { label: 'Doctor-Led Fitness', to: '/doctor-led-fitness', desc: 'Medically supervised fitness for safe, sustainable weight and metabolic improvement.' },
      { label: 'Online Fitness Coaching', to: '/online-fitness-coaching', desc: 'Personalized training that fits your body, health history and routine.' },
    ],
    articles: [
      { label: 'Diabetes Diet Plan: Indian Foods, Meals & What to Eat', to: '/blog/diabetes-diet-plan-indian-foods' },
      { label: 'Diabetes Diet for Parents: Helping Mother or Father', to: '/diabetes-diet-for-parents' },
      { label: 'Prediabetes Diet and Lifestyle', to: '/blog/prediabetes-diet-and-lifestyle' },
      { label: 'Insulin Resistance: Symptoms, Diet & Exercise', to: '/blog/insulin-resistance-symptoms-diet-exercise' },
      { label: 'Why Am I Always Tired?', to: '/blog/why-am-i-always-tired' },
      { label: 'Sedentary Lifestyle: Health Effects', to: '/blog/sedentary-lifestyle-health-effects' },
      { label: 'Walking for Better Health', to: '/blog/walking-for-better-health' },
      { label: 'How Much Exercise Do You Need?', to: '/blog/how-much-exercise-do-you-need' },
    ],
    calculators: [
      { label: 'BMI Calculator', to: '/calculators/bmi' },
      { label: 'BMI Prime', to: '/calculators/bmi-prime' },
      { label: 'TDEE Calculator', to: '/calculators/tdee' },
      { label: 'Calorie Deficit', to: '/calculators/calorie-deficit' },
      { label: 'Body Fat %', to: '/calculators/body-fat' },
      { label: 'Waist-to-Hip Ratio', to: '/calculators/waist-to-hip' },
    ],
    tool: { label: 'Full Body Health Analysis', to: '/health-analysis' },
    faqs: [
      { question: 'Can nutrition really help manage diabetes?', answer: 'Evidence-based nutrition and lifestyle change, guided alongside your treating doctor, can support blood sugar control. Any medication changes should always be made by your treating physician.' },
      { question: 'Do I need to follow an extreme diet to lose weight?', answer: 'No. Our approach focuses on practical, sustainable changes rather than restrictive diets, which is why results tend to last. Individual results vary.' },
      { question: 'Where should I start?', answer: 'Begin with the Full Body Health Analysis or a free consultation. From there we recommend the most suitable program for your health profile.' },
    ],
  },

  'womens-reproductive-health': {
    slug: 'womens-reproductive-health',
    icon: 'HeartPulse',
    eyebrow: 'Health Hub',
    title: "Women's & Reproductive Health",
    gradient: 'from-pink-500 to-rose-500',
    accent: 'text-pink-400',
    badgeBg: 'bg-pink-500/10',
    badgeBorder: 'border-pink-500/30',
    metaTitle: "Women's & Reproductive Health Hub | PMOS & Fertility Nutrition",
    metaDescription: "Doctor-guided support for PMOS (previously called PCOS), hormonal balance and fertility nutrition. Explore programs, guides and calculators from TCUBE FITNESS.",
    intro: 'Specialized, gentle and evidence-based support for hormonal health, PMOS and fertility nutrition, coordinated with our gynecology team at every life stage.',
    programs: [
      { label: 'Fertility Nutrition Program', to: '/fertility-nutrition', desc: 'Nutrition and lifestyle support for couples planning conception, with reproductive-health focus.' },
      { label: 'PMOS Support & Diet', to: '/pcos-diet-plan', desc: 'Doctor-guided nutrition for PMOS (previously called PCOS) and hormonal balance.' },
    ],
    articles: [
      { label: 'PCOS and Weight: Insulin, Nutrition & Sustainable Change', to: '/blog/pcos-weight-management-nutrition' },
      { label: 'Preconception Nutrition for Couples', to: '/blog/preconception-nutrition-couples' },
      { label: 'Fertility Nutrition: Foods for Hormone Balance', to: '/blog/fertility-nutrition-hormone-balance' },
      { label: 'PMOS Diet Plan: Complete Guide', to: '/blog/pcos-diet-plan-complete-guide' },
    ],
    calculators: [
      { label: 'Waist-to-Hip Ratio', to: '/calculators/waist-to-hip' },
      { label: 'BMI Calculator', to: '/calculators/bmi' },
    ],
    faqs: [
      { question: 'Is this suitable if I am trying to conceive?', answer: 'Yes. Our Fertility Nutrition Program supports individuals and couples planning conception through doctor-guided nutrition and lifestyle care. Results vary based on individual medical factors.' },
      { question: 'Can nutrition help with PMOS?', answer: 'Nutrition and lifestyle are central to managing PMOS symptoms. Our plans are personalized and coordinated with medical care.' },
    ],
  },

  'pregnancy-postpartum': {
    slug: 'pregnancy-postpartum',
    icon: 'Baby',
    eyebrow: 'Health Hub',
    title: 'Pregnancy & Postpartum',
    gradient: 'from-blue-500 to-cyan-500',
    accent: 'text-blue-400',
    badgeBg: 'bg-blue-500/10',
    badgeBorder: 'border-blue-500/30',
    metaTitle: 'Pregnancy & Postpartum Hub | Trimester Nutrition & Recovery',
    metaDescription: 'Trimester-specific pregnancy nutrition and safe postpartum recovery, supervised by our medical team. Explore programs, guides and calculators from TCUBE FITNESS.',
    intro: 'Safe, nutrient-focused support through every trimester and into recovery after childbirth, always alongside your obstetric care.',
    programs: [
      { label: 'Pregnancy Nutrition Plan', to: '/pregnancy-nutrition', desc: 'Trimester-specific nutrition for maternal and fetal health, under medical supervision.' },
      { label: 'Postpartum Recovery Program', to: '/postpartum-recovery', desc: 'Recovery-focused, breastfeeding-friendly nutrition and a gradual return to fitness.' },
    ],
    articles: [
      { label: 'Gestational Diabetes Diet: Food & Blood Sugar in Pregnancy', to: '/blog/gestational-diabetes-diet-pregnancy' },
      { label: 'Best Diet Plan for Pregnancy', to: '/blog/best-diet-plan-for-pregnancy' },
      { label: 'Best Diet Plan After Delivery', to: '/blog/best-diet-plan-after-delivery' },
    ],
    calculators: [
      { label: 'Pregnancy Weight Gain', to: '/calculators/pregnancy-weight-gain' },
      { label: 'Water Intake', to: '/calculators/water-intake' },
      { label: 'BMI Calculator', to: '/calculators/bmi' },
    ],
    faqs: [
      { question: 'Can I join during pregnancy?', answer: 'Yes. Our Pregnancy Nutrition Plan provides trimester-specific guidance under medical supervision. We always recommend continuing routine care with your obstetrician alongside our support.' },
      { question: 'When can I start postpartum support?', answer: 'Our postpartum program is designed to begin gently after childbirth, with medical oversight throughout your recovery.' },
    ],
  },

  'fitness-body-transformation': {
    slug: 'fitness-body-transformation',
    icon: 'Dumbbell',
    eyebrow: 'Health Hub',
    title: 'Fitness & Body Transformation',
    gradient: 'from-orange-500 to-amber-500',
    accent: 'text-orange-400',
    badgeBg: 'bg-orange-500/10',
    badgeBorder: 'border-orange-500/30',
    metaTitle: 'Fitness & Body Transformation Hub | Safe, Doctor-Led Training',
    metaDescription: 'Safe, progressive training and nutrition with medical oversight. Explore fitness programs, guides and free performance calculators from TCUBE FITNESS.',
    intro: 'Build strength, stamina and a body composition you can sustain, with progressive training and nutrition guided by medical oversight.',
    programs: [
      { label: 'Online Fitness Coaching', to: '/online-fitness-coaching', desc: 'Personalized workouts for home or gym with weekly coaching and progress tracking.' },
      { label: 'Doctor-Led Fitness', to: '/doctor-led-fitness', desc: 'Injury-aware, medically supervised training for lasting transformation.' },
    ],
    articles: [
      { label: 'Calorie Deficit for Weight Loss: A Doctor\u2019s Guide', to: '/blog/weight-loss-calorie-deficit-explained' },
      { label: 'Indian Diet for Weight Management', to: '/blog/indian-diet-for-weight-management' },
      { label: 'Visceral Fat & Waist Size: Why They Matter', to: '/blog/visceral-fat-waist-size-health' },
      { label: 'Strength Training After 40', to: '/blog/strength-training-after-40' },
      { label: 'Weekend Sports Nutrition & Warm-Up', to: '/blog/weekend-sports-nutrition-warmup-guide' },
      { label: 'How Much Protein Per Day (Indian Diet)', to: '/blog/how-much-protein-per-day-indian-diet' },
      { label: 'Types of Protein Powder Compared', to: '/blog/types-of-protein-powder-comparison' },
      { label: 'Prawns vs Fish vs Chicken Protein', to: '/blog/prawns-fish-chicken-protein-comparison' },
    ],
    calculators: [
      { label: 'One Rep Max', to: '/calculators/one-rep-max' },
      { label: 'Heart Rate Zones', to: '/calculators/heart-rate-zones' },
      { label: 'VO₂ Max', to: '/calculators/vo2-max' },
      { label: 'FFMI', to: '/calculators/ffmi' },
      { label: 'Protein Intake', to: '/calculators/protein-intake' },
      { label: 'Macro Calculator', to: '/calculators/macro' },
    ],
    faqs: [
      { question: 'Is medically supervised fitness different?', answer: 'Yes. Training that accounts for your health history, injuries and conditions is safer and more effective than generic plans, especially over 40 or with medical conditions.' },
      { question: 'Do you support home workouts?', answer: 'Yes. Programs are tailored for home or gym, with progression and weekly coaching either way.' },
    ],
  },
};

export const HUB_ORDER = [
  'metabolic-weight-health',
  'womens-reproductive-health',
  'pregnancy-postpartum',
  'fitness-body-transformation',
];

export default CONTENT_HUBS;
